import { useState, useMemo, useEffect } from "react";

export const useUserState = (
  orgDashboardStore,
  dashboardStore,
  userCode,
  expanded,
  pinned
) => {
  const [heartRate, setHeartRate] = useState(null);
  const [hrv, setHrv] = useState(null);
  const [motion, setMotion] = useState(null);
  const [color, setColor] = useState("inactive");
  const [alertLabel, setAlertLabel] = useState(null);
  const [introspection, setIntrospection] = useState(null);
  const [progress, setProgress] = useState(null);

  const colorFromStatus = (status) => {
    if (status === "red" || status === "green" || status === "yellow") {
      return status;
    }
    return "inactive";
  };

  useEffect(() => {
    dashboardStore.addSubscription(userCode);
  }, []);

  useEffect(() => {
    let color = "inactive";
    if (orgDashboardStore.isActive[userCode]) {
      if (orgDashboardStore.status[userCode]) {
        const status = orgDashboardStore.status[userCode];
        color = colorFromStatus(status);
      }
    }

    if (orgDashboardStore.alertLabel[userCode]) {
      const alertLabel = orgDashboardStore.alertLabel[userCode];
      setAlertLabel(alertLabel);
    }

    if (orgDashboardStore.introspection[userCode]) {
      const introspection = orgDashboardStore.introspection[userCode];
      setIntrospection(introspection);
    }
    setColor(color);
  }, [
    userCode,
    orgDashboardStore.isActive,
    orgDashboardStore.status,
    orgDashboardStore.counter,
    orgDashboardStore.alertLabel,
    orgDashboardStore.introspection,
  ]);

  useEffect(() => {
    const heartRate = orgDashboardStore.heartRate[userCode];
    setHeartRate(heartRate);
  }, [userCode, orgDashboardStore.heartRate, orgDashboardStore.counter]);

  useEffect(() => {
    const hrv = orgDashboardStore.hrv[userCode];
    setHrv(hrv);
  }, [userCode, orgDashboardStore.hrv, orgDashboardStore.counter]);

  useEffect(() => {
    const motion = orgDashboardStore.motion[userCode];
    setMotion(motion);
  }, [userCode, orgDashboardStore.motion, orgDashboardStore.counter]);

  useEffect(() => {
    if (!introspection) {
      return;
    }
    const completeness = introspection["data_completeness"];
    const matched = introspection["match_percentile"];
    const progress1 = Math.min(
      1,
      completeness[0] / completeness[1] / completeness[2]
    );
    const progress2 = Math.min(1, matched[0] / matched[1] / matched[2]);
    setProgress((progress1 + progress2) / 2);
  }, [introspection]);

  let level = 4;
  if (expanded || pinned || color === "red") {
    level = 1;
  } else if (color === "yellow") {
    level = 2;
  } else if (color === "green") {
    level = 2;
  } else if (color === "inactive" && heartRate !== undefined) {
    level = 2;
  }
  const className = [
    "UserCircle__profile",
    `UserCircle__profile_${color}`,
    `UserCircle__profile_level_${level}`,
    pinned || expanded ? "UserCircle__profile_pinned" : "",
  ].join(" ");

  const colorForHeartRate = useMemo(() => {
    let color = "inactive";
    if (!heartRate || !orgDashboardStore.isActive[userCode]) {
      return color;
    }

    const classification = heartRate.classification;

    if (classification === "no-data" || classification === "unknown") {
      color = "inactive";
    } else if (classification === "high" || classification === "low") {
      color = "yellow";
    } else if (
      classification === "very-low" ||
      classification === "very-high"
    ) {
      color = "red";
    } else if (classification === "normal") {
      color = "green";
    }
    return color;
  }, [heartRate, orgDashboardStore.isActive, userCode]);
  return {
    heartRate,
    hrv,
    motion,
    color,
    alertLabel,
    introspection,
    progress,
    colorForHeartRate,
    className,
    level,
  };
};

export const colorMap = {
  "very-low": "#F26A6A",
  low: "#EEDF5A",
  normal: "#5AD876",
  high: "#F26A6A",
  "very-high": "#F26A6A",
  red: "#F26A6A",
  yellow: "#EEDF5A",
  green: "#5AD876",
  "-": "#D9D9D9",
};

export const fillInBarData = (dict, values, name) => {
  const filtered = dict.filter((x) => x[name] !== null);
  values.forEach((value) => {
    const contains = filtered.find((x) => x[name] === value);
    if (!contains) {
      const record = {
        count: 0,
      };
      record[name] = value;
      filtered.push(record);
    }
  });
  const filteredWithColor = filtered.map((x) => {
    return {
      ...x,
      color: colorMap[x[name]],
      name: x[name],
    };
  });
  const ordered = [];
  values.forEach((value) => {
    const record = filteredWithColor.find((x) => x[name] === value);
    ordered.push(record);
  });
  return ordered;
};
